import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Edit2, Check, AlertTriangle } from 'lucide-react';

const DOC_TYPE_LABELS = {
  huurovereenkomst: 'Huurovereenkomst',
  contract: 'Contract',
  keuring: 'Keuringsrapport',
  factuur: 'Factuur',
  vergunning: 'Vergunning',
  offerte: 'Offerte',
  overig: 'Overig',
};

const DOC_TYPE_COLORS = {
  huurovereenkomst: 'bg-blue-50 text-blue-700 border-blue-200',
  contract: 'bg-purple-50 text-purple-700 border-purple-200',
  keuring: 'bg-amber-50 text-amber-700 border-amber-200',
  factuur: 'bg-green-50 text-green-700 border-green-200',
  vergunning: 'bg-red-50 text-red-700 border-red-200',
  offerte: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  overig: 'bg-slate-50 text-slate-600 border-slate-200',
};

const SECTION_LABELS = {
  parties: 'Partijen',
  financials: 'Financieel',
  dates: 'Datums',
  conditions: 'Voorwaarden',
};

const FIELD_LABELS = {
  verhuurder: 'Verhuurder',
  huurder: 'Huurder',
  object: 'Gehuurde object',
  leverancier: 'Leverancier',
  inspecteur: 'Inspecteur',
  amount: 'Bedrag',
  monthly: 'Maandbedrag',
  frequency: 'Betaalfrequentie',
  btw: 'BTW',
  bankrekening: 'Bankrekening',
  factuurnummer: 'Factuurnummer',
  start: 'Ingangsdatum',
  end: 'Einddatum',
  opzeg_voor: 'Opzeggen voor',
  indexatie: 'Indexatiedatum',
  vervaldatum: 'Vervaldatum',
  looptijd: 'Looptijd',
  verlenging: 'Verlenging',
  opzegtermijn: 'Opzegtermijn',
  indexatie_methode: 'Indexatiemethode',
  waarborgsom: 'Waarborgsom',
  nutsvoorzieningen: 'Nutsvoorzieningen',
  resultaat: 'Resultaat',
  bevindingen: 'Bevindingen',
  type_keuring: 'Type keuring',
  verlenende_instantie: 'Verlenende instantie',
  scope: 'Scope',
  geldigheid: 'Geldigheid',
};

function getFieldLabel(key) {
  return FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && value.toLowerCase() === 'null') return null;
  return String(value);
}

function FieldRow({ label, value, editable, onChange, source }) {
  if (!editable && !formatValue(value)) return null;

  const inferred = source === 'inferred';

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 py-1.5">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium sm:w-32 flex-shrink-0 sm:pt-0.5 flex items-center gap-1 flex-wrap">
        {label}
        {inferred && (
          <span
            data-testid="field-inferred-badge"
            title="Automatisch berekend/afgeleid — controleren"
            className="inline-flex items-center gap-0.5 normal-case tracking-normal text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            controleren
          </span>
        )}
      </span>
      {editable ? (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 px-2 py-1 rounded-md border bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 ${inferred ? 'border-amber-300' : 'border-slate-200'}`}
        />
      ) : (
        <span className="text-xs text-slate-700 flex-1">{formatValue(value)}</span>
      )}
    </div>
  );
}

export default function KeyDataSection({ keyData, editable = false, onChange, title }) {
  const [open, setOpen] = useState(true);

  if (!keyData || typeof keyData !== 'object') return null;

  const docType = keyData.document_type;
  const typeStyle = DOC_TYPE_COLORS[docType] || DOC_TYPE_COLORS.overig;
  const typeLabel = DOC_TYPE_LABELS[docType] || docType;

  const hasContent = Object.entries(keyData).some(([k, v]) => {
    if (k === 'document_type') return false;
    if (typeof v === 'object' && v !== null) {
      if (Array.isArray(v)) return v.length > 0;
      return Object.values(v).some(val => formatValue(val));
    }
    return formatValue(v);
  });

  if (!hasContent && !editable) return null;

  const handleFieldChange = (section, field, value) => {
    if (!onChange) return;
    const updated = { ...keyData };
    if (section === 'root') {
      updated[field] = value;
    } else if (section === 'special_clauses') {
      const clauses = [...(updated.special_clauses || [])];
      clauses[field] = value;
      updated.special_clauses = clauses;
    } else {
      updated[section] = { ...(updated[section] || {}), [field]: value };
    }
    onChange(updated);
  };

  const sections = ['parties', 'financials', 'dates', 'conditions'];
  const fieldSources = (keyData.field_sources && typeof keyData.field_sources === 'object') ? keyData.field_sources : {};

  return (
    <div
      data-testid="key-data-section"
      className="bg-white rounded-xl border border-slate-200/80 overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
        data-testid="key-data-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <FileText className="w-3.5 h-3.5 text-[#2E75B6]" />
          {title || (docType === 'huurovereenkomst' ? 'Contractgegevens' : 'Documentgegevens')}
          {docType && (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${typeStyle}`}>
              {typeLabel}
            </span>
          )}
          {editable && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
              <Edit2 className="w-2.5 h-2.5 inline mr-0.5" />
              Bewerkbaar
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 space-y-4 pt-3">
          {sections.map(section => {
            const data = keyData[section];
            if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
            const entries = Object.entries(data);
            const visibleEntries = editable ? entries : entries.filter(([, v]) => formatValue(v));
            if (visibleEntries.length === 0) return null;

            return (
              <div key={section}>
                <p className="text-[10px] uppercase tracking-wider text-[#2E75B6] font-semibold mb-1.5 flex items-center gap-1.5">
                  {SECTION_LABELS[section] || section}
                </p>
                <div className="bg-slate-50/50 rounded-lg border border-slate-100 px-3 divide-y divide-slate-100">
                  {visibleEntries.map(([key, val]) => (
                    <FieldRow
                      key={key}
                      label={getFieldLabel(key)}
                      value={val}
                      editable={editable}
                      source={fieldSources[section]?.[key]}
                      onChange={v => handleFieldChange(section, key, v)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Special clauses */}
          {((keyData.special_clauses && keyData.special_clauses.length > 0) || editable) && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#2E75B6] font-semibold mb-1.5">
                Bijzondere bepalingen
              </p>
              <div className="bg-slate-50/50 rounded-lg border border-slate-100 px-3 py-2 space-y-1.5">
                {(keyData.special_clauses || []).map((clause, idx) => (
                  editable ? (
                    <input
                      key={idx}
                      type="text"
                      value={clause}
                      onChange={e => handleFieldChange('special_clauses', idx, e.target.value)}
                      className="w-full px-2 py-1 rounded-md border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30"
                    />
                  ) : (
                    <p key={idx} className="text-xs text-slate-600 leading-relaxed flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2E75B6] mt-1.5 flex-shrink-0" />
                      {clause}
                    </p>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
